<script setup>
// FLOW-06 (SPEC S2 e S5; Task 6.0, depois 6.5) — o "Executar agora" da lista de Flows: roda a versão
// publicada de um flow sem agente, de verdade. O formulário dos `params` é o dos dados do teste do
// editor (`CamposDoTeste`, com o estado de `criarDadosDoTeste`), gerado do `args_schema` da
// publicada; a conversa é um número opcional, porque o flow que lê a conversa só roda com uma (R7).
//
// Confirmar confere os campos como o editor confere (`conferir`: o foco vai ao primeiro errado) e
// emite `executar({params, conversation_id, chave_de_idempotencia})`. A chave nasce uma vez por
// abertura: o segundo clique manda a mesma, e o servidor devolve a mesma execução em vez de rodar de
// novo (S2). Quem usa a tela faz o pedido, liga `executando` enquanto espera e, na recusa de leigo do
// servidor (422, `{mensagem, como_resolver, campo}`), devolve o erro em `recusa`: ele aparece no
// campo que nomeia (um param, ou a conversa), ou embaixo do formulário. Mexer no que foi recusado
// tira o aviso.
//
// O formulário abre com os params da última execução deste flow pela aba (decisão do Murilo, 25/10;
// quem busca é a tela, na Task 6.5). O valor que voltou redigido não entra no campo, que fica vazio:
// o PowerTools troca o segredo por `$TOOL_SECRET` por valor, também no meio de um texto, e mandar a
// marca de volta rodaria o flow com ela no lugar do segredo.
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import AvisoDoFlow from '../settings/powerTools/flow/AvisoDoFlow.vue';
import CamposDoTeste from '../settings/powerTools/flow/CamposDoTeste.vue';
import { criarDadosDoTeste } from '../settings/powerTools/flow/dadosDoTeste';
import { semOpcionaisVazios } from '../settings/powerTools/flow/regrasDoParametro';
import { SEGREDO } from '../settings/powerTools/flow/sessaoDeTeste';
import { humanizar } from '../settings/powerTools/usoDoSegredo';

const props = defineProps({
  // O item da lista de Flows: `{nome, publicada: {versao, args_schema}}`.
  flow: { type: Object, required: true },
  // Os params da última execução deste flow pela aba (invocador `manual`); sem eles, vazios.
  paramsIniciais: { type: Object, default: () => ({}) },
  executando: { type: Boolean, default: false },
  // A recusa de leigo do servidor (o `erro` do 422), ou nula.
  recusa: { type: Object, default: null },
});

const emit = defineEmits(['executar', 'fechar']);

const { t } = useI18n();

// O `args_schema` da publicada de volta à forma do `params` do YAML, que o formulário lê: a lista
// `required` do topo vira o booleano de cada param (o inverso do `esquemaDoParam` do compilador). O
// `object` guarda a lista dele, e é sempre obrigatório.
const definicoes = computed(() => {
  const schema = props.flow.publicada?.args_schema || {};
  const obrigatorios = schema.required || [];
  return Object.fromEntries(
    Object.entries(schema.properties || {}).map(([nome, def]) => [
      nome,
      def.type === 'object'
        ? def
        : { ...def, required: obrigatorios.includes(nome) },
    ])
  );
});

// Os params iniciais sem o que voltou redigido: o campo do segredo abre vazio.
const semRedigidos = params =>
  Object.fromEntries(
    Object.entries(params || {}).filter(
      ([, valor]) => !JSON.stringify(valor ?? null).includes(SEGREDO)
    )
  );

const dialogo = ref(null);
const campos = ref(null);
const dados = ref(null);
const conversa = ref('');
const chave = ref('');
const recusaVista = ref(null);

// Cada abertura começa do zero: o formulário, a conversa e a chave.
function abrir() {
  dados.value = criarDadosDoTeste({
    paramsDeExemplo: () => semRedigidos(props.paramsIniciais),
    chavesDeContexto: () => [],
    definicoes: () => definicoes.value,
  });
  conversa.value = '';
  chave.value = crypto.randomUUID();
  recusaVista.value = props.recusa;
  dialogo.value?.open();
}
const fechar = () => dialogo.value?.close();
defineExpose({ abrir, fechar });

// -- A recusa: no campo que ela nomeia, ou embaixo do formulário
watch(
  () => props.recusa,
  valor => {
    recusaVista.value = valor;
  }
);
const campoDaRecusa = computed(() => recusaVista.value?.campo || null);
const naConversa = computed(() => campoDaRecusa.value === 'conversation_id');
const emUmParam = computed(
  () => !!campoDaRecusa.value && campoDaRecusa.value in definicoes.value
);
const textoDaRecusa = computed(() => {
  const recusa = recusaVista.value;
  if (!recusa) return '';
  return recusa.como_resolver
    ? `${recusa.mensagem} ${t('POWER_TOOLS.POWERFLOW.DETAIL.HOW_TO_FIX', {
        texto: recusa.como_resolver,
      })}`
    : recusa.mensagem;
});
const errosDeFora = computed(() =>
  emUmParam.value ? { [campoDaRecusa.value]: textoDaRecusa.value } : {}
);
const recusaSolta = computed(
  () => !!recusaVista.value && !naConversa.value && !emUmParam.value
);

// Mexer no que foi recusado tira o aviso: a conversa, o da conversa; um param, o dos params. A recusa
// sem campo sai com qualquer mudança.
watch(conversa, () => {
  if (naConversa.value || recusaSolta.value) recusaVista.value = null;
});
watch(
  () => dados.value?.paramsTexto,
  (agora, antes) => {
    if (antes === undefined) return;
    if (emUmParam.value || recusaSolta.value) recusaVista.value = null;
  }
);

// -- Confirmar
async function confirmar() {
  if (props.executando) return;
  const errado = await campos.value?.conferir();
  if (errado) return;
  const numero = conversa.value.replace(/\D/g, '');
  emit('executar', {
    params: semOpcionaisVazios(definicoes.value, dados.value.params.valor),
    conversation_id: numero || null,
    chave_de_idempotencia: chave.value,
  });
}
</script>

<template>
  <Dialog
    ref="dialogo"
    width="xl"
    overflow-y-auto
    :title="
      t('POWER_TOOLS.POWERFLOW.EXECUTE_NOW.TITLE', {
        flow: humanizar(flow.nome),
      })
    "
    :description="
      t('POWER_TOOLS.POWERFLOW.EXECUTE_NOW.DESCRIPTION', {
        versao: flow.publicada?.versao,
      })
    "
    :confirm-button-label="t('POWER_TOOLS.POWERFLOW.EXECUTE_NOW.CONFIRM')"
    :is-loading="executando"
    data-executar-agora
    @confirm="confirmar"
    @close="emit('fechar')"
  >
    <div v-if="dados" class="flex flex-col gap-5">
      <section class="flex flex-col gap-3" data-params>
        <h4 class="mb-0 text-sm font-medium text-n-slate-12">
          {{ t('POWER_TOOLS.POWERFLOW.EXECUTE_NOW.PARAMS') }}
        </h4>
        <CamposDoTeste
          ref="campos"
          :dados="dados"
          prefixo="executar"
          :erros-de-fora="errosDeFora"
        />
      </section>

      <div class="flex flex-col gap-1.5" data-conversa>
        <Input
          v-model="conversa"
          size="sm"
          inputmode="numeric"
          autocomplete="off"
          :label="t('POWER_TOOLS.POWERFLOW.EXECUTE_NOW.CONVERSATION')"
          :placeholder="
            t('POWER_TOOLS.POWERFLOW.EXECUTE_NOW.CONVERSATION_PLACEHOLDER')
          "
          :message="
            naConversa
              ? ''
              : t('POWER_TOOLS.POWERFLOW.EXECUTE_NOW.CONVERSATION_HINT')
          "
        />
        <AvisoDoFlow
          v-if="naConversa"
          nivel="erro"
          solto
          role="alert"
          data-recusa-na-conversa
        >
          <p class="m-0 text-sm">{{ recusaVista.mensagem }}</p>
          <p v-if="recusaVista.como_resolver" class="m-0 text-n-slate-11">
            {{
              t('POWER_TOOLS.POWERFLOW.DETAIL.HOW_TO_FIX', {
                texto: recusaVista.como_resolver,
              })
            }}
          </p>
        </AvisoDoFlow>
      </div>

      <AvisoDoFlow v-if="recusaSolta" nivel="erro" role="alert" data-recusa>
        <p class="m-0 text-sm">{{ recusaVista.mensagem }}</p>
        <p v-if="recusaVista.como_resolver" class="m-0 text-n-slate-11">
          {{
            t('POWER_TOOLS.POWERFLOW.DETAIL.HOW_TO_FIX', {
              texto: recusaVista.como_resolver,
            })
          }}
        </p>
      </AvisoDoFlow>
    </div>
  </Dialog>
</template>
